import React, { useMemo } from 'react';
import classNames from 'classnames';

import { useActiveTableCallback } from '../../../../../element/table/store/hotTableStoreHooks';
import {
    updateHotTableSelectedCellTextAlignment,
    updateHotTableSelectedCellVerticalAlignment,
} from '../../../../../element/table/utils/hotInstanceUtils/cell/hotTableCellUpdateUtils';
import Button from '../../../../../components/buttons/Button';
import Icon from '../../../../../components/icons/Icon';
import { TextAlignment, VerticalAlignment } from '../../../../../../common/table/CellTypeConstants';

export type TableCellAlignmentOptionsProps = {
    commonTextAlignment: string | undefined;
    commonVerticalAlignment: string | undefined;
    onOptionClick?: () => void;
    layout?: 'stacked' | 'inline';
};

const TEXT_ALIGNMENT_OPTIONS = [
    {
        alignment: TextAlignment.LEFT,
        iconName: 'toolbar-align-left',
    },
    {
        alignment: TextAlignment.CENTER,
        iconName: 'toolbar-align-center',
    },
    {
        alignment: TextAlignment.RIGHT,
        iconName: 'toolbar-align-right',
    },
];

const VERTICAL_ALIGNMENT_OPTIONS = [
    {
        alignment: VerticalAlignment.TOP,
        iconName: 'toolbar-align-top',
    },
    {
        alignment: VerticalAlignment.MIDDLE,
        iconName: 'toolbar-align-middle',
    },
    {
        alignment: VerticalAlignment.BOTTOM,
        iconName: 'toolbar-align-bottom',
    },
];

const TableCellAlignmentOptions: React.FC<TableCellAlignmentOptionsProps> = ({
    commonTextAlignment,
    commonVerticalAlignment,
    onOptionClick,
    layout = 'stacked',
}) => {
    const updateCellTextAlignment = useActiveTableCallback(
        (activeTable, textAlignment: TextAlignment) => {
            updateHotTableSelectedCellTextAlignment(activeTable.instance, textAlignment);
            onOptionClick?.();
        },
        [onOptionClick],
    );

    const updateCellVerticalAlignment = useActiveTableCallback(
        (activeTable, verticalAlignment: VerticalAlignment) => {
            updateHotTableSelectedCellVerticalAlignment(activeTable.instance, verticalAlignment);
            onOptionClick?.();
        },
        [onOptionClick],
    );

    const textAlignmentOptions = useMemo(
        () =>
            TEXT_ALIGNMENT_OPTIONS.map(({ alignment, iconName }) => (
                <li key={alignment}>
                    <Button
                        className={classNames('cell-alignment-options-button', {
                            active: commonTextAlignment === alignment,
                        })}
                        onClickFn={() => updateCellTextAlignment(alignment)}
                    >
                        <Icon name={iconName} />
                    </Button>
                </li>
            )),
        [commonTextAlignment, updateCellTextAlignment],
    );

    const verticalAlignmentOptions = useMemo(
        () =>
            VERTICAL_ALIGNMENT_OPTIONS.map(({ alignment, iconName }) => (
                <li key={alignment}>
                    <Button
                        className={classNames('cell-alignment-options-button', {
                            active: commonVerticalAlignment === alignment,
                        })}
                        onClickFn={() => updateCellVerticalAlignment(alignment)}
                    >
                        <Icon name={iconName} />
                    </Button>
                </li>
            )),
        [commonVerticalAlignment, updateCellVerticalAlignment],
    );

    if (layout === 'inline') {
        return (
            <ul className="cell-alignment-options cell-alignment-options-inline">
                {textAlignmentOptions}
                {verticalAlignmentOptions}
            </ul>
        );
    }

    return (
        <>
            <ul className="cell-alignment-options">{textAlignmentOptions}</ul>
            <div className="divider" />
            <ul className="cell-alignment-options">{verticalAlignmentOptions}</ul>
        </>
    );
};

export default TableCellAlignmentOptions;
